import React, { Fragment, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import { Refresh as RetryIcon } from '@mui/icons-material';
import {
    createAutomarkerTargetTemplate,
    getAutomarkerTargetTemplate,
    updateAutomarkerTargetTemplate
} from '@dyl/apiClients/backendApi';
import createSnackbarAction from '@dyl/utils/createSnackbarAction';
import { useTitle } from '@dyl/systems/TitleManager';
import { Button, CircularProgress, FormHelperText, TextField, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import useChangeEvent from '@dyl/hooks/useChangeEvent';
import LoadingScreen from '@dyl/components/LoadingScreen';
import ChipList from '@dyl/components/ChipList';
import ScreenSaveCancelBlock from '@dyl/blocks/ScreenSaveCancelBlock';
import useExitPrompt from '@dyl/hooks/useExitPrompt';
import { useLevels } from "@dyl/systems/LevelsManager";
import checkTargets from "@dyl/apiClients/backendApi/checkTargets";
import SpecialSymbolStrip from "@dyl/utils/specialSymbolStrip";

const useStyles = makeStyles(theme => ({
    name: {
        marginBottom: theme.spacing(3),
    },

    groupHeader: {
        marginBottom: theme.spacing(1),
    },
    group: {
        marginBottom: theme.spacing(6),
    },
}));

export default function AutomarkerTargetTemplateEditor() {
    const { templateId } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const classes = useStyles();
    const { globalLevel } = useLevels();

    const isNew = !templateId;

    const [isLoading, setLoading] = useState(!isNew);

    const [name, setName] = useState('');
    const onNameChange = useChangeEvent(setName);

    const [primaryTargets, setPrimaryTargets] = useState([]);
    const [secondaryTargets, setSecondaryTargets] = useState([]);

    // Set already saved data as valid initially.
    const [isChecking, setChecking] = useState(false);
    const [targetDictionary, setTargetDictionary] = useState({});
    const onCheck = async() => {
        setChecking(true);
        const data = await checkTargets(primaryTargets.concat(secondaryTargets));
        const results = Object.fromEntries(data.map(x => [x.target, x.valid]));
        const _haveErrors = [
            primaryTargets.some(t => !results[t]),
            secondaryTargets.some(t => !results[t])
        ];
        setHaveErrors(_haveErrors);
        setTargetDictionary(results);
        setChecking(false);
    };

    const [validated, setValidated] = useState(false);
    const [haveErrors, setHaveErrors] = useState([false, false]);
    useEffect(() => {
        if (Object.entries(targetDictionary).length === 0) return;
        const data = primaryTargets.concat(secondaryTargets);
        setValidated(data.every(t => !!targetDictionary[t]));
        console.log(data);
        const _haveErrors = [
            primaryTargets.some(t => !targetDictionary[t]),
            secondaryTargets.some(t => !targetDictionary[t])
        ];
        setHaveErrors(_haveErrors);
    }, [primaryTargets, secondaryTargets, targetDictionary]);


    const title = (
        name || (
            isNew ? 'New Template'
            : isLoading ? 'Loading...'
            : 'Unnamed Template'
        )
    );
    useTitle({ breadcrumbs: ['Automarker Target Templates', title] });
    useExitPrompt();

    useEffect(() => {
        if (isNew) return;

        let cancelled = false;

        (async () => {
            const template = await (async () => {
                while (!cancelled) {
                    try {
                        return await getAutomarkerTargetTemplate(templateId);
                    }
                    catch {
                        await new Promise(resolve => enqueueSnackbar(
                            'Failed to load template', {
                                variant: 'error',
                                persist: true,
                                action: createSnackbarAction({
                                    iconComponent: RetryIcon,
                                    onClick: resolve,
                                }),
                            }
                        ));
                    }
                }
            })();

            const {
                name,
                primary_targets,
                secondary_targets,
            } = template;


            setName(name);
            setPrimaryTargets(primary_targets?.filter(x => !!x) ?? []);
            setSecondaryTargets(secondary_targets?.filter(x => !!x) ?? []);

            setLoading(false);
        })();

        return () => cancelled = true;
    }, [templateId]);

    const [isSaving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);

        const indicator = enqueueSnackbar('Saving...', {
            variant: 'info',
            persist: true,
            action: createSnackbarAction({
                iconComponent: () => <CircularProgress size='1em' style={{ color: '#fff' }} />,
            }),
        });

        try {
            if (isNew) {
                const { target_template_id } = await createAutomarkerTargetTemplate({
                    name,
                    primary_targets: primaryTargets,
                    secondary_targets: secondaryTargets,
                });

                navigate(`/${globalLevel.label}/amtt/${target_template_id}`, {
                    state: { ignoreExitPrompt: true },
                });
            }
            else {
                await updateAutomarkerTargetTemplate(templateId, {
                    name,
                    // NB: These arrays must be null if they would be empty
                    primary_targets: primaryTargets?.length > 0 ? primaryTargets : null,
                    secondary_targets: secondaryTargets?.length > 0 ? secondaryTargets : null,
                });
            }

            enqueueSnackbar('Template saved', { variant: 'success' });
        }
        catch (error) {
            enqueueSnackbar('Failed to save template', { variant: 'error' });
            console.error(error)
        }
        finally {
            setSaving(false);
            closeSnackbar(indicator);
        }
    };

    return (
        <>
            <LoadingScreen open={isLoading} />

            <TextField
                className={classes.name}
                value={name}
                onChange={onNameChange}
                label='Template name'
                required
                variant='filled'
            />

            {[
                { hasErrors: haveErrors[0], header: 'Primary Targets', value: primaryTargets, set: setPrimaryTargets },
                { hasErrors: haveErrors[1], header: 'Secondary Targets', value: secondaryTargets, set: setSecondaryTargets },
            ].map(({ hasErrors, header, value, set }, index) => (
                <Fragment key={index}>
                    <Typography variant='h6' className={classes.groupHeader}>
                        {header}
                    </Typography>

                    {!!hasErrors &&
                        <FormHelperText error>
                            Some of the targets that were input are not valid with the automarker.
                            Please amend or delete them above before continuing.
                        </FormHelperText>
                    }

                    <ChipList
                        className={classes.group}
                        value={value}
                        onChange={set}
                        disabledColor={null}
                        targetDictionary={targetDictionary}
                        transformFunction={SpecialSymbolStrip}
                    />
                </Fragment>
            ))}

            <Button variant='contained' color='primary' disabled={validated} onClick={onCheck}>
                Check Targets
                { isChecking && <> &nbsp; <CircularProgress color="inherit" size={12} /> </>}
            </Button>

            <FormHelperText>
                Note: Updating a template does *not* update tasks which have previously imported the template.
            </FormHelperText>

            <ScreenSaveCancelBlock
                save={save}
                cancelTo={`/${globalLevel.label}/amtt`}
                disabled={isSaving || !validated}
            />
        </>
    );
}